import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  CircleHelp,
  ClipboardList,
  RefreshCw,
  Search,
  Settings,
  X,
  XCircle,
} from 'lucide-react';
import type { useTruthProfitabilityPanel } from '../../hooks/useTruthProfitabilityPanel';
import { usePeriodOrderDetailCheckpoint } from '../../hooks/usePeriodOrderDetailCheckpoint';
import { Badge, type BadgeTone } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import type {
  TruthComputeJobRecord,
  TruthComputeScope,
  TruthDataSourceInfo,
} from '../../types/profitabilityTruth';
import { profitabilityComputePhaseDetail } from '../../utils/profitabilityComputePhase';
import {
  OrderDetailEnrichButton,
  OrderDetailEnrichStatusBanners,
} from '../fleetDataHub/OrderDetailEnrichButton';

type PanelState = ReturnType<typeof useTruthProfitabilityPanel>;

interface TruthProfitabilityPipelinePanelProps {
  panel: PanelState;
  compact?: boolean;
  className?: string;
  /** Optional profitability assumptions control (shown on the sources / GPS Mileage row). */
  assumptionsActive?: boolean;
  onAssumptionsClick?: () => void;
}

const COMPUTE_SCOPE_OPTIONS: Array<{
  scope: TruthComputeScope;
  label: string;
  description: string;
}> = [
  { scope: 'all', label: 'All', description: 'Vehicle Day → Orders → Stops/maps → Customer' },
  { scope: 'vehicle-day', label: 'Vehicle Day', description: 'Merged vehicle-day costs only (no map stops)' },
  { scope: 'order', label: 'Order', description: 'Orders, telematics stops/maps, and order costs' },
  { scope: 'customer', label: 'Customer', description: 'Customer daily profitability' },
];

function scopeLabel(scope: TruthComputeScope | undefined): string {
  const found = COMPUTE_SCOPE_OPTIONS.find((o) => o.scope === (scope ?? 'all'));
  return found?.label ?? 'All';
}

function topGapLabels(gapCounts: Record<string, number>, limit = 3): string[] {
  return Object.entries(gapCounts)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([code, count]) => `${code} (${count})`);
}

function isProbeDisabledWarning(warning: string): boolean {
  return /source probing disabled/i.test(warning);
}

function SourceStatusIcon({ status }: { status: TruthDataSourceInfo['status'] }) {
  if (status === 'available') {
    return <CheckCircle className="w-3 h-3 text-success" aria-hidden />;
  }
  if (status === 'unavailable') {
    return <XCircle className="w-3 h-3 text-destructive" aria-hidden />;
  }
  return <CircleHelp className="w-3 h-3 text-muted-foreground" aria-hidden />;
}

function sourceStatusClass(status: TruthDataSourceInfo['status']): string {
  if (status === 'available') return 'text-success';
  if (status === 'unavailable') return 'text-destructive';
  return 'text-muted-foreground';
}

function JobRow({
  job,
  onCancel,
  cancelling,
}: {
  job: TruthComputeJobRecord;
  onCancel?: (jobId: string) => void;
  cancelling?: boolean;
}) {
  const pct =
    job.progress?.percentComplete ??
    (job.status === 'completed' ? 100 : job.status === 'pending' ? 0 : null);
  const recovering =
    job.status === 'failed' &&
    job.error?.includes('corrupted') &&
    job.progress?.lastProgressAt != null &&
    Date.now() - new Date(job.progress.lastProgressAt).getTime() < 5 * 60 * 1000;
  const displayStatus = recovering ? 'running' : job.status;
  const statusTone: BadgeTone =
    displayStatus === 'completed'
      ? 'success'
      : displayStatus === 'failed'
        ? 'destructive'
        : displayStatus === 'cancelled'
          ? 'warning'
          : displayStatus === 'running'
            ? 'info'
            : 'neutral';
  const barColour =
    displayStatus === 'completed'
      ? 'bg-success'
      : displayStatus === 'failed'
        ? 'bg-destructive'
        : displayStatus === 'cancelled'
          ? 'bg-warning'
          : 'bg-ring';
  const phaseDetail =
    (displayStatus === 'running' || recovering) && job.progress
      ? profitabilityComputePhaseDetail(job.progress)
      : null;
  const canCancel =
    Boolean(onCancel) && (job.status === 'pending' || job.status === 'running');

  return (
    <div className="px-3 py-2 border-b border-border/50 last:border-b-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <Badge tone={statusTone}>
            {recovering ? 'recovering' : job.status}
          </Badge>
          <span className="text-xs text-foreground font-mono">
            {job.startDate} → {job.endDate}
          </span>
          <Badge tone="neutral">
            {scopeLabel(job.scope)}
          </Badge>
          {job.recordCount != null && (
            <span className="text-xs text-muted-foreground">{job.recordCount.toLocaleString()} records</span>
          )}
        </div>
        <span className="text-xs text-accent font-mono shrink-0">
          {new Date(job.createdAt).toLocaleString()}
        </span>
      </div>
      {job.status === 'failed' && job.error && !recovering && (
        <p className="mt-1 text-xs text-destructive break-all">{job.error}</p>
      )}
      {recovering && (
        <p className="mt-1 text-xs text-warning/90">
          Metadata was interrupted by a restart; compute is still running — follow the progress bar.
        </p>
      )}
      {phaseDetail && (
        <p className="mt-1 text-xs text-ring/90">{phaseDetail}</p>
      )}
      {pct != null && (
        <div className="mt-1.5 flex items-center gap-2">
          <div className="h-1 flex-1 bg-secondary rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${barColour}`}
              style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground font-mono tabular-nums w-8 text-right">
            {Math.round(pct)}%
          </span>
          {canCancel && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={cancelling}
              onClick={() => onCancel?.(job.jobId)}
              className="h-auto px-1.5 py-0.5 border-destructive/60 text-destructive hover:bg-destructive/40 hover:text-destructive"
            >
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </Button>
          )}
        </div>
      )}
      {pct == null && canCancel && (
        <div className="mt-1.5 flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={cancelling}
            onClick={() => onCancel?.(job.jobId)}
            className="h-auto px-1.5 py-0.5 border-destructive/60 text-destructive hover:bg-destructive/40 hover:text-destructive"
          >
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Truth pipeline visibility: source status, coverage summary, compute + jobs.
 */
export default function TruthProfitabilityPipelinePanel({
  panel,
  compact = false,
  className = '',
  assumptionsActive = false,
  onAssumptionsClick,
}: TruthProfitabilityPipelinePanelProps) {
  const {
    statusData,
    statusLoading,
    sourcesChecking,
    sourcesProbed,
    statusError,
    statusWindow,
    lastComputed,
    computeJobs,
    computeJobsLoading,
    computeTriggerLoading,
    computeTriggerError,
    recomputeWarning,
    pendingComputeScope,
    showJobsPanel,
    setShowJobsPanel,
    hasActiveJob,
    conflictingCompletedJob,
    handleCompute,
    doCompute,
    cancelJob,
    cancellingJobIds,
    setRecomputeWarning,
    checkSources,
    refreshJobs,
  } = panel;

  const [computeMenuOpen, setComputeMenuOpen] = useState(false);
  const computeMenuRef = useRef<HTMLDivElement>(null);

  const orderCheckpoint = usePeriodOrderDetailCheckpoint(
    statusWindow.startDate,
    statusWindow.endDate,
  );

  const handleCheckSources = () => {
    void checkSources();
    void orderCheckpoint.runCheck();
  };

  useEffect(() => {
    if (!computeMenuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (computeMenuRef.current && !computeMenuRef.current.contains(event.target as Node)) {
        setComputeMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setComputeMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [computeMenuOpen]);

  const coverage = statusData?.coverage_summary;
  const gapHints = coverage?.gap_counts ? topGapLabels(coverage.gap_counts) : [];
  const warnings =
    !statusLoading && statusData?.warnings?.length
      ? statusData.warnings.filter((w) => !isProbeDisabledWarning(w))
      : [];

  return (
    <div className={`space-y-2 ${className}`}>
      <div
        className={`rounded-lg border border-ring/30 bg-ring/10 ${
          compact ? 'p-2.5' : 'p-3'
        }`}
      >
        {orderCheckpoint.checking && (
          <p className="text-xs text-muted-foreground mb-2 animate-pulse">
            Checking order details for period…
          </p>
        )}
        {orderCheckpoint.checkError && !orderCheckpoint.checking && (
          <p className="text-xs text-warning mb-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            Could not check order details: {orderCheckpoint.checkError}
          </p>
        )}
        {orderCheckpoint.hasChecked
          && !orderCheckpoint.checking
          && orderCheckpoint.blankDetailsCount > 0 && (
          <div className="mb-2 rounded-lg border border-warning/40 bg-warning/20 px-3 py-2 space-y-2">
            <p className="text-xs text-warning">
              {orderCheckpoint.blankDetailsCount}{' '}
              {orderCheckpoint.blankDetailsCount === 1 ? 'order' : 'orders'} in this period{' '}
              {orderCheckpoint.blankDetailsCount === 1 ? 'is' : 'are'} missing
              customer/origin/destination. Filling details improves order and customer
              profitability quality. Each click processes about 10 orders; use Continue
              for the next batch.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <OrderDetailEnrichButton
                blankDetailsCount={orderCheckpoint.blankDetailsCount}
                missingDetailsCount={orderCheckpoint.missingDetailsCount}
                bgEnrichPending={orderCheckpoint.bgEnrichPending}
                bgEnrichPaused={orderCheckpoint.bgEnrichPaused}
                onStartOrContinue={orderCheckpoint.startOrContinueEnrichBatch}
                onPause={orderCheckpoint.pauseEnrichBatch}
              />
            </div>
            <OrderDetailEnrichStatusBanners
              bgEnrichPaused={orderCheckpoint.bgEnrichPaused}
              bgEnrichPending={orderCheckpoint.bgEnrichPending}
              pipelineGateDepths={orderCheckpoint.pipelineGateDepths}
              enrichUsed={orderCheckpoint.enrichUsed}
              enrichBudget={orderCheckpoint.enrichBudget}
              onContinueNow={() => orderCheckpoint.startOrContinueEnrichBatch()}
              pipelineSafeDepth={orderCheckpoint.PIPELINE_SAFE_DEPTH}
              userPaused={orderCheckpoint.userPaused}
            />
          </div>
        )}
        {orderCheckpoint.hasChecked
          && !orderCheckpoint.checking
          && orderCheckpoint.blankDetailsCount === 0
          && !orderCheckpoint.checkError && (
          <p className="text-xs text-success/90 mb-2">
            Order details look complete for this period.
          </p>
        )}

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className={`font-medium text-ring ${compact ? 'text-xs' : 'text-sm'}`}>
              Truth layer pipeline
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 font-mono">
              {statusWindow.startDate} → {statusWindow.endDate}
            </p>
            {statusLoading && (
              <p className="text-xs text-muted-foreground mt-1 animate-pulse">
                Loading pipeline status for {statusWindow.startDate} → {statusWindow.endDate}…
              </p>
            )}
            {sourcesChecking && (
              <p className="text-xs text-muted-foreground mt-1 animate-pulse">
                Checking event availability (may take a minute on wide ranges)…
              </p>
            )}
            {statusError && (
              <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                {statusError}
              </p>
            )}
            {!statusLoading && statusData?.telematics_stops && (
              <p className="text-xs text-muted-foreground mt-1">
                Map overlays (last compute
                {statusData.telematics_stops.start_date && statusData.telematics_stops.end_date
                  ? ` ${statusData.telematics_stops.start_date}→${statusData.telematics_stops.end_date}`
                  : ''}
                ):{' '}
                {(statusData.telematics_stops.record_count ?? 0) > 0
                  ? `${statusData.telematics_stops.record_count} stops`
                    + (statusData.telematics_stops.vehicle_days_with_stops != null
                      ? ` across ${statusData.telematics_stops.vehicle_days_with_stops} vehicle-days`
                      : '')
                    + (statusData.telematics_stops.overlaps_window === false
                      ? ' — outside this window'
                      : '')
                  : 'none yet — run Order or All compute after GPS ingest'}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleCheckSources}
              disabled={statusLoading || sourcesChecking || orderCheckpoint.checking}
              title="Count Iceberg events and check order-detail enrichment for this period"
              className="border border-border disabled:bg-muted/50 disabled:opacity-100"
            >
              <Search
                className={`w-3.5 h-3.5 ${
                  sourcesChecking || orderCheckpoint.checking ? 'animate-pulse' : ''
                }`}
              />
              {sourcesChecking || orderCheckpoint.checking
                ? 'Checking…'
                : sourcesProbed || orderCheckpoint.hasChecked
                  ? 'Re-check sources'
                  : 'Check sources'}
            </Button>
            <div className="relative" ref={computeMenuRef}>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => setComputeMenuOpen((open) => !open)}
                disabled={computeTriggerLoading}
                aria-haspopup="menu"
                aria-expanded={computeMenuOpen}
                className="disabled:bg-muted disabled:opacity-100"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${computeTriggerLoading ? 'animate-spin' : ''}`} />
                {computeTriggerLoading ? 'Queuing…' : 'Compute'}
                <ChevronDown className="w-3.5 h-3.5 opacity-90" />
              </Button>
              {computeMenuOpen && !computeTriggerLoading && (
                <div
                  role="menu"
                  className="absolute right-0 z-20 mt-1 w-64 rounded-md border border-border bg-popover shadow-lg overflow-hidden"
                >
                  {COMPUTE_SCOPE_OPTIONS.map((option) => (
                    <Button
                      key={option.scope}
                      type="button"
                      variant="ghost"
                      role="menuitem"
                      onClick={() => {
                        setComputeMenuOpen(false);
                        handleCompute(option.scope);
                      }}
                      className="w-full h-auto text-left justify-start px-3 py-2 border-b border-border/60 last:border-b-0 hover:bg-secondary/80"
                    >
                      <span className="block text-xs font-medium text-foreground">{option.label}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        {option.description}
                      </span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowJobsPanel((v) => !v)}
              className="relative border border-border"
            >
              <ClipboardList className="w-3.5 h-3.5 text-ring" />
              Jobs
              {hasActiveJob && (
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-ring animate-pulse" />
              )}
            </Button>
          </div>
        </div>

        {(statusData && !statusLoading) || onAssumptionsClick ? (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
              {statusData && !statusLoading
                ? statusData.sources.map((src) => (
                    <div
                      key={src.event_type}
                      className="flex items-center gap-1"
                      title={
                        src.status === 'unknown'
                          ? 'Not checked yet — use Check sources'
                          : src.status === 'available'
                            ? `${src.event_count ?? 0} events`
                            : 'No events in this period'
                      }
                    >
                      <SourceStatusIcon status={src.status} />
                      <span className={`text-xs ${sourceStatusClass(src.status)}`}>
                        {src.source.split('(')[0].trim()}
                      </span>
                    </div>
                  ))
                : null}
            </div>
            {onAssumptionsClick ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onAssumptionsClick}
                className={cn(
                  'border shrink-0 ml-auto',
                  assumptionsActive
                    ? 'bg-warning/20 border-warning/50 text-warning hover:bg-warning/20'
                    : 'border-border hover:border-warning'
                )}
                title="Edit fuel, driver, and standing cost assumptions"
              >
                <Settings className="w-3.5 h-3.5 text-warning" />
                Assumptions
              </Button>
            ) : null}
          </div>
        ) : null}
        {statusData && !statusLoading && !sourcesProbed && !sourcesChecking && (
          <p className="text-xs text-muted-foreground mt-1.5">
            Source dots and order-detail counts stay unchecked until you run Check sources
            (optional).
          </p>
        )}
        {coverage && !statusLoading && (
          <p className="text-xs text-muted-foreground mt-2">
            Coverage:{' '}
            <span className="text-foreground font-medium">
              {coverage.profitability_ready_vehicle_days}/{coverage.total_vehicle_days}
            </span>{' '}
            vehicle-days profitability-ready
            {gapHints.length > 0 && (
              <span className="text-warning/90"> — gaps: {gapHints.join(', ')}</span>
            )}
          </p>
        )}
        {lastComputed.computedAt && !statusLoading && (
          <p className="text-xs text-muted-foreground mt-1">
            Last computed: {new Date(lastComputed.computedAt).toLocaleString()}
            {lastComputed.recordCount != null && ` (${lastComputed.recordCount} records)`}
          </p>
        )}
        {!lastComputed.computedAt && statusData && !statusLoading && (
          <p className="text-xs text-warning mt-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            No stored projection for this period — run Compute (vehicle-days → orders → customer).
          </p>
        )}

        {warnings.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {warnings.map((w, i) => (
              <span key={i} className="text-xs text-warning flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                {w}
              </span>
            ))}
          </div>
        )}
      </div>

      {recomputeWarning && conflictingCompletedJob && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-warning/20 border border-warning/40 rounded-lg text-xs">
          <AlertTriangle className="w-4 h-4 shrink-0 text-warning" />
          <span className="text-warning flex-1">
            A completed {scopeLabel(pendingComputeScope)} job exists for{' '}
            {conflictingCompletedJob.startDate} → {conflictingCompletedJob.endDate}. Run again?
          </span>
          <Button
            type="button"
            variant="warning"
            onClick={() => {
              void doCompute(pendingComputeScope);
            }}
            disabled={computeTriggerLoading}
            className="h-auto px-2 py-1 text-white"
          >
            Run Again
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setRecomputeWarning(false)}
            className="h-auto px-2 py-1"
          >
            Cancel
          </Button>
        </div>
      )}

      {showJobsPanel && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-secondary/50 border-b border-border">
            <span className="text-xs font-semibold text-foreground">Projection jobs</span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => void refreshJobs()}
                className="h-auto px-0 text-xs"
              >
                {computeJobsLoading ? 'Refreshing…' : 'Refresh'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={() => setShowJobsPanel(false)}
                className="p-0.5"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          {computeTriggerError && (
            <div className="px-3 py-2 bg-destructive/20 border-b border-destructive/30 text-xs text-destructive">
              {computeTriggerError}
            </div>
          )}
          <div className="max-h-48 overflow-y-auto">
            {computeJobs.length === 0 && !computeJobsLoading && (
              <p className="px-3 py-4 text-xs text-muted-foreground text-center">
                No jobs yet. Click Compute to queue a projection for this period.
              </p>
            )}
            {computeJobs.map((job) => (
              <JobRow
                key={job.jobId}
                job={job}
                onCancel={(jobId) => void cancelJob(jobId)}
                cancelling={cancellingJobIds.has(job.jobId)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
